import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  WritableSignal,
  OnInit,
} from '@angular/core';
import { LnPrimaryButtonComponent } from '@components/butons/ln-primary-button/ln-primary-button.component';
import { MainProductCardComponent } from '@components/cards/main-product-card/main-product-card.component';
import { SpecialProductInterface } from '@interfaces/products/special_product_interface';
import { ProductsService } from '@core/services/products/products.service';
import { ProductServiceParamsInterface } from '@interfaces/products/product_service_params_interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from '@interfaces/api/api.interface';
import { GetSpecialProductsData } from '@interfaces/products/get_special_products_data';
import { TranslocoPipe } from '@jsverse/transloco';
import { DeviceScreenFamilyService } from '@core/services/device-screen-family/device-screen-family.service';
import { SortFilter } from '@enums/sortFilter';
import { SelectedFilterService } from '@core/services/selected-filter/selectedFilter.service';
import { LnProductFilterComponent } from '@components/ln-product-filter/ln-product-filter.component';
import { NgClass } from '@angular/common';
import { ObjectDataState } from '../../states/object-data-State';
import { SpecialProductsObjectDataStateInterface } from '@interfaces/special-products/special-products-object-data-state-interface';

@Component({
  selector: 'ln-all-special-created-products',
  imports: [LnPrimaryButtonComponent, MainProductCardComponent, TranslocoPipe, LnProductFilterComponent, NgClass],
  templateUrl: './all-special-created-products.component.html',
  styleUrl: './all-special-created-products.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllSpecialCreatedProductsComponent implements OnInit {
  readonly TAG = 'ArtisansDesignersCreatedAndSoldProducts';
  products: WritableSignal<SpecialProductInterface[]> = signal<SpecialProductInterface[]>([]);
  private priceFilterSelected = true;
  pageNumber = 1;
  pageSize = 24;
  showMoreProducts = signal(false);
  protected showMoreProductsButonSpinner: WritableSignal<boolean> = signal(false);
  protected gettingProducts: WritableSignal<boolean> = signal(true);
  private productService = inject(ProductsService);
  private destroyRef = inject(DestroyRef);
  private deviceScreenFamily = inject(DeviceScreenFamilyService);
  protected currentScreenSize = this.deviceScreenFamily.currentDeviceScreenFamily;
  private selectedFilterService = inject(SelectedFilterService);
  private objectDataState: ObjectDataState<SpecialProductsObjectDataStateInterface> = inject(ObjectDataState);
  selectedFilter: WritableSignal<SortFilter> = signal<SortFilter>(SortFilter.mayorPrecio);
  initialFilter: SortFilter = SortFilter.mayorPrecio;
  private restoringPreviousState = false;
  constructor() {
    effect(() => {
      const filter = this.selectedFilter();
      if (filter != this.initialFilter && !this.restoringPreviousState) {
        this.initialFilter = filter;
        this.selectPriceOrDateFilter(filter);
        this.getProducts();
      }
    });
  }

  ngOnInit(): void {
    this.restoreObjectDataState();
  }

  updateObjectDataState() {
    const specialProductInterface: SpecialProductsObjectDataStateInterface = {
      selectedFilter: this.selectedFilter(),
      pageNumber: this.pageNumber,
      showMoreProducts: this.showMoreProducts(),
    };
    this.objectDataState.setData(specialProductInterface);
  }

  restoreObjectDataState() {
    const specialProductData: SpecialProductsObjectDataStateInterface | undefined = this.objectDataState.getData();
    if (specialProductData) {
      this.restoringPreviousState = true;
      this.selectedFilter.set(specialProductData.selectedFilter);
      this.selectPriceOrDateFilter(this.selectedFilter());
      this.pageNumber = specialProductData.pageNumber;
      this.showMoreProducts.set(specialProductData.showMoreProducts);
    }
    this.getProducts();
    this.restoringPreviousState = false;
  }

  getProducts() {
    // console.log(`${this.TAG} getProducts(getMoreProducts= ${getMoreProducts})`);
    const querryParams: ProductServiceParamsInterface = this.setupQuerryParams();
    //si se produjo un cambio de filtro o se esta restableciendo el estado anterior de la pagina
    let usedPageSize = this.pageSize;
    if (this.pageNumber > 1) {
      querryParams.pageSize = this.pageNumber * this.pageSize;
      usedPageSize = querryParams.pageSize;
      querryParams.pageNumber = 1;
      this.products.set([]);
    }
    this.gettingProducts.set(true);
    this.showMoreProducts.set(false);
    this.productService
      .getProducts(querryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<GetSpecialProductsData>) => {
          const productos = res.data.productos;
          const total = res.data.total;
          this.products.set(productos);
          if (productos.length >= usedPageSize && usedPageSize * this.pageNumber < total) {
            this.showMoreProducts.set(true);
          } else {
            this.showMoreProducts.set(false);
          }
          this.updateObjectDataState();
          this.gettingProducts.set(false);
        },
        error: err => {
          console.log(`${this.TAG} ${err}`);
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
      });
  }

  getMoreProducts() {
    this.showMoreProductsButonSpinner.set(true);
    this.pageNumber++;
    const querryParams: ProductServiceParamsInterface = this.setupQuerryParams();
    this.productService
      .getProducts(querryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<GetSpecialProductsData>) => {
          const productos = res.data.productos;
          const total = res.data.total;
          this.products.set([...this.products(), ...productos]);
          if (productos.length >= this.pageSize && this.pageSize * this.pageNumber < total) {
            this.showMoreProducts.set(true);
          } else {
            this.showMoreProducts.set(false);
          }
          this.updateObjectDataState();
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
        error: err => {
          console.log(`${this.TAG} ${err}`);
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
      });
  }

  setupQuerryParams(): ProductServiceParamsInterface {
    return this.priceFilterSelected
      ? {
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          priceOrder: this.selectedFilterService.getOrderBySelectedFilter(this.selectedFilter()),
          // this.getOrderBySelectedFilter(this.selectedFilter()),
          isPiezaUnica: false,
        }
      : {
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          dateOrder: this.selectedFilterService.getOrderBySelectedFilter(this.selectedFilter()),
          // this.getOrderBySelectedFilter(this.selectedFilter()),
          isPiezaUnica: false,
        };
  }

  selectPriceOrDateFilter(selectedFilterValue: SortFilter) {
    this.priceFilterSelected = this.selectedFilterService.selectPriceOrDateFilter(selectedFilterValue);
  }
}
